import React, { useState } from "react";
import cx from "clsx";
import styles from "./planCard.module.scss";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { useCurrentUserQuery } from "../../../../../../api/queries/useUserQuery";
import { useChangePlanMutation } from "../../../../../../api/chartSmart/useChangePlanMutation";
import { useCancelPlanMutation } from "../../../../../../api/chartSmart/useCancelPlanMutation";
import { Button } from "../../../../../../shared/Button/Button";
import { uiNotification } from "../../../../../../services/UINotificationService";
import { ConfirmModal } from "../../../../../../boxes/ConfirmModal/ConfirmModal";
import { CHARTSMART_PLAN, CHARTSMART_PLAN_WEIGHTS } from "../../../../../../consts/ChartSmart";
import { extractApiError } from "../../../../../../utilities/api";

export function PlanCard({
  plan,
  frequency,
  planName,
  currentPlan,
  nextMonthPlan,
  getSubscrtiptions,
  showLoader,
  hideLoader
}) {
  const { tSettings } = useAppTranslation.Settings();
  const { data: user } = useCurrentUserQuery();
  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
  const currentPlanWeight = currentPlan ? CHARTSMART_PLAN_WEIGHTS[currentPlan] : 0;

  const changePlan = useChangePlanMutation({
    onSuccess: () => {
      getSubscrtiptions();

      const message = !currentPlan ? "subscriptions.success.select" :
        CHARTSMART_PLAN_WEIGHTS[planName] > currentPlanWeight ?
          "subscriptions.success.upgrade" :
          "subscriptions.success.downgrade";

      uiNotification.success(tSettings(message));
    },
    onError: (res) => {
      const resMessage = extractApiError(res);

      const message = !currentPlan ? "subscriptions.error.select" :
        CHARTSMART_PLAN_WEIGHTS[planName] > currentPlanWeight ?
          "subscriptions.error.upgrade" :
          "subscriptions.error.downgrade";

      uiNotification.error(tSettings([`subscriptions.error.${resMessage}`, message]));
    },
    onSettled: () => {
      hideLoader();
    }
  });

  const cancelPlan = useCancelPlanMutation({
    onSuccess: () => {
      getSubscrtiptions();
      uiNotification.success(tSettings("subscriptions.success.cancel"));
    },
    onError: () => {
      uiNotification.error(tSettings("subscriptions.error.cancel"));
    },
    onSettled: () => {
      hideLoader();
    }
  });

  const limit = planName === CHARTSMART_PLAN.unlimited ? "unlimited" : plan?.limit;
  const subscriptionPrice = plan?.price / 100 || 0;
  const isActive = currentPlan === planName;

  const onCancelPlan = () => {
    setIsCancelConfirmModalOpen(false);
    showLoader();
    cancelPlan.mutateAsync();
  }

  const onChangePlan = () => {
    showLoader();
    changePlan.mutateAsync({
      planName,
      payg: false
    });
  }

  const showNote = currentPlan &&
    nextMonthPlan &&
    currentPlan !== nextMonthPlan &&
    nextMonthPlan === planName;

  return (
    <div className={cx(styles.wrapper,
      {
        [styles.active]: isActive
      }
    )}>
      <div className={styles.inner}>
        <div className={styles.title}>{tSettings(`subscriptions.chartSmart.${planName}`)}</div>
        <div className={styles.textWrapper}>
          <div className={styles.textLimit}>{tSettings(`subscriptions.chartSmart.description.upTo`, {
            limit: limit
          })}
          </div>
          <div className={styles.textBody}>{tSettings(`subscriptions.chartSmart.description.${planName}`)}</div>
        </div>
        <div className={styles.subscription}>{user?.currencySymbol}{subscriptionPrice} per {frequency} / Account</div>
      </div>
      {showNote && (
        <div className={styles.note}>
          <i>
            {tSettings("subscriptions.chartSmart.planNote")}
          </i>
        </div>
      )}
      <div className={styles.inner}>
        {isActive ? (
          <Button
            size="small"
            color="error"
            className={styles.button}
            onClick={() => setIsCancelConfirmModalOpen(true)}
          >
            {tSettings("subscriptions.chartSmart.cancelPlan")}
          </Button>
        ) : (
          <Button
            size="small"
            className={styles.button}
            onClick={() => onChangePlan()}
          >
            {tSettings("subscriptions.chartSmart.selectPlan")}
          </Button>
        )}
        <div className={styles.disclaimer}>{tSettings("subscriptions.chartSmart.unused")}</div>
      </div>
      {isCancelConfirmModalOpen && (
        <ConfirmModal
          isOpen
          withAlertIcon
          title={tSettings("subscriptions.chartSmart.confirmCancel.title")}
          onConfirm={onCancelPlan}
          onClose={() => setIsCancelConfirmModalOpen(false)}
          onCancel={() => setIsCancelConfirmModalOpen(false)}
          isConfirming={cancelPlan.isLoading}
          cancelTitle={tSettings("subscriptions.chartSmart.confirmCancel.cancel")}
        >
          <div>
            <p>
              {tSettings("subscriptions.chartSmart.confirmCancel.desc")}
            </p>
            <p>
              {tSettings("subscriptions.chartSmart.confirmCancel.desc2")}
            </p>
          </div>
        </ConfirmModal>
      )}
    </div>
  )
}
